/**
 * Degree-aware note spelling.
 *
 * Every other module in this app used to keep its own twelve-name lookup table
 * and index it with a pitch class. A pitch class knows a note is "the black key
 * between D and E"; it does NOT know whether to print D♯ or E♭. That decision
 * belongs to the *degree*, not the pitch.
 *
 * The rule this file implements is the one Music Theory 1 teaches by name: a
 * scale uses every letter exactly once, in order, and the accidental is whatever
 * makes that letter land on the right pitch. Spell the 3rd of C minor and you get
 * E♭ — never D♯ — because the letter after D must be E.
 *
 * Everything here is pure and side-effect free, so it is safe to call during render.
 */

export const LETTERS = ["C", "D", "E", "F", "G", "A", "B"] as const;
export type Letter = (typeof LETTERS)[number];

const LETTER_PC: Record<Letter, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** Semitones above the tonic for degrees 1..7 of a major scale. */
const MAJOR_SEMIS = [0, 2, 4, 5, 7, 9, 11];

const mod12 = (n: number) => ((n % 12) + 12) % 12;

/* ── accidentals ─────────────────────────────────────────────────────── */

/** Render an alteration (-2..+2) as a glyph. Unicode by default; ASCII for VexFlow/Tone. */
export function accidental(alter: number, ascii = false): string {
  const flat = ascii ? "b" : "♭";
  const sharp = ascii ? "#" : "♯";
  if (alter <= -2) return flat.repeat(-alter);
  if (alter === -1) return flat;
  if (alter === 0) return "";
  return sharp.repeat(alter);
}

export interface ParsedNote {
  letter: Letter;
  /** -2 = double flat … +2 = double sharp. */
  alter: number;
  /** Pitch class 0..11. */
  pc: number;
}

/** Parse "Eb", "E♭", "F##", "C" → letter + alteration + pitch class. */
export function parseNote(name: string): ParsedNote | null {
  const m = name
    .trim()
    .replace(/♯/g, "#")
    .replace(/♭/g, "b")
    .match(/^([A-Ga-g])(#{1,2}|b{1,2}|)$/);
  if (!m) return null;
  const letter = m[1].toUpperCase() as Letter;
  const acc = m[2];
  const alter = acc.startsWith("#") ? acc.length : acc.startsWith("b") ? -acc.length : 0;
  return { letter, alter, pc: mod12(LETTER_PC[letter] + alter) };
}

/** Build a name from a letter + alteration. */
export function noteName(letter: Letter, alter: number, ascii = false): string {
  return `${letter}${accidental(alter, ascii)}`;
}

/* ── degrees ─────────────────────────────────────────────────────────── */

export interface ParsedDegree {
  /** 1..7 (or beyond — 9, 11, 13 fold back into the octave). */
  number: number;
  /** Alteration relative to the major scale's version of that degree. */
  alter: number;
}

/** Parse "1", "♭3", "#4", "♭♭7", "b5" → degree number + alteration. */
export function parseDegree(label: string): ParsedDegree | null {
  const m = label
    .trim()
    .replace(/♯/g, "#")
    .replace(/♭/g, "b")
    .match(/^(#{1,2}|b{1,2}|)(\d{1,2})$/);
  if (!m) return null;
  const acc = m[1];
  return {
    number: parseInt(m[2], 10),
    alter: acc.startsWith("#") ? acc.length : acc.startsWith("b") ? -acc.length : 0,
  };
}

/**
 * Spell one scale degree above a tonic.
 *
 *   spellDegree("C", "♭3")  → "E♭"      (never D♯ — the letter after D must be E)
 *   spellDegree("C", "♭5")  → "G♭"      (the blues note, sharing G with the natural 5)
 *   spellDegree("F♯", "7")  → "E♯"      (F♯ major's sixth sharp)
 *   spellDegree("G♭", "4")  → "C♭"
 *
 * Returns null if the tonic can't be parsed. Alterations beyond a double
 * sharp/flat are clamped, which only happens in keys nobody writes.
 */
export function spellDegree(tonic: string, degreeLabel: string | number, ascii = false): string | null {
  const t = parseNote(tonic);
  const d = parseDegree(String(degreeLabel));
  if (!t || !d) return null;

  // Fold 9/11/13 back into 2/4/6 — an extension is its simple degree an octave up.
  const simple = ((d.number - 1) % 7 + 7) % 7; // 0-based degree index
  const letter = LETTERS[(LETTERS.indexOf(t.letter) + simple) % 7];

  // Where this degree must land, as a pitch class.
  const targetPc = mod12(t.pc + MAJOR_SEMIS[simple] + d.alter);
  // Where the bare letter already sits.
  const naturalPc = LETTER_PC[letter];

  // The accidental is the shortest signed distance between the two.
  let alter = targetPc - naturalPc;
  if (alter > 6) alter -= 12;
  if (alter < -6) alter += 12;
  alter = Math.max(-2, Math.min(2, alter));

  return noteName(letter, alter, ascii);
}

/**
 * Spell a whole scale from its degree labels — exactly the labels the lessons
 * already carry, e.g. ["1","♭3","4","♭5","5","♭7"] for the blues scale.
 */
export function spellScale(tonic: string, degreeLabels: (string | number)[], ascii = false): string[] {
  return degreeLabels.map((d) => spellDegree(tonic, d, ascii) ?? String(d));
}

/* ── key signatures ──────────────────────────────────────────────────── */

/** Major keys written with flats. Everything else is written with sharps. */
const FLAT_MAJOR = new Set(["F", "Bb", "Eb", "Ab", "Db", "Gb", "Cb"]);
/** Minor keys written with flats — note these are NOT the same tonics as above. */
const FLAT_MINOR = new Set(["D", "G", "C", "F", "Bb", "Eb", "Ab"]);

/**
 * Does this key signature use flats?
 *
 * The distinction matters: C **major** takes sharps (it has none, so the sharp
 * table's naturals are right), but C **minor** takes three flats. Deciding this
 * from the tonic letter alone — as the app used to — spells C minor as
 * C D D♯ F G G♯ A♯.
 */
export function keyUsesFlats(tonic: string, isMinor = false): boolean {
  const t = parseNote(tonic);
  if (!t) return false;
  const ascii = noteName(t.letter, t.alter, true);
  if (t.alter < 0) return true; // any flat-named tonic
  if (t.alter > 0) return false; // any sharp-named tonic
  return (isMinor ? FLAT_MINOR : FLAT_MAJOR).has(ascii);
}

/** Fallback speller for a pitch class with no degree context. */
export function spellPc(pc: number, useFlats: boolean, ascii = false): string {
  const p = mod12(pc);
  const SHARP: [Letter, number][] = [
    ["C", 0], ["C", 1], ["D", 0], ["D", 1], ["E", 0], ["F", 0],
    ["F", 1], ["G", 0], ["G", 1], ["A", 0], ["A", 1], ["B", 0],
  ];
  const FLAT: [Letter, number][] = [
    ["C", 0], ["D", -1], ["D", 0], ["E", -1], ["E", 0], ["F", 0],
    ["G", -1], ["G", 0], ["A", -1], ["A", 0], ["B", -1], ["B", 0],
  ];
  const [letter, alter] = (useFlats ? FLAT : SHARP)[p];
  return noteName(letter, alter, ascii);
}

/**
 * Spell a pitch class *in the context of a scale*: if the pitch belongs to the
 * scale, it is spelled by its degree; otherwise it falls back to the key's
 * accidental preference. This is what makes `nameForPc(pcFromDegree(3))` come
 * out as E♭ in C minor without every call site having to know about degrees.
 */
export function spellPcInScale(
  tonic: string,
  degreeLabels: (string | number)[],
  pc: number,
  useFlats: boolean,
  ascii = false
): string {
  const t = parseNote(tonic);
  if (t) {
    const target = mod12(pc);
    for (const d of degreeLabels) {
      const name = spellDegree(tonic, d, ascii);
      const parsed = name ? parseNote(name) : null;
      if (parsed && parsed.pc === target) return name!;
    }
  }
  return spellPc(pc, useFlats, ascii);
}

/* ── octaves ─────────────────────────────────────────────────────────── */

/**
 * Attach an octave number to a spelled note so it can be played, choosing the
 * octave from the *sounding pitch* rather than the letter — so B♯ and C♭ land
 * in the register a musician expects.
 */
export function withOctave(name: string, octave: number): string {
  return `${name}${octave}`;
}

/** MIDI number for a spelled note in a given octave (C4 = 60). */
export function toMidi(name: string, octave: number): number | null {
  const p = parseNote(name);
  if (!p) return null;
  return (octave + 1) * 12 + LETTER_PC[p.letter] + p.alter;
}

/** Convert any spelling to the ASCII form Tone.js and VexFlow expect. */
export function toAscii(name: string): string {
  return name.replace(/♯/g, "#").replace(/♭/g, "b");
}
